import Link from "next/link";
import { formatBytes, formatEta, formatSpeed } from "@/lib/upload-with-progress";

export type UploadProgressStatus =
  | "queued"
  | "hashing"
  | "checking"
  | "duplicate"
  | "presigning"
  | "uploading"
  | "downloading"
  | "extracting"
  | "creating"
  | "done"
  | "complete"
  | "error";

type UploadProgressBarProps = {
  fileName: string;
  kind?: "video" | "magazine" | "thumbnail";
  status: UploadProgressStatus;
  loaded?: number;
  total?: number;
  bytesPerSec?: number;
  etaSeconds?: number;
  errorMessage?: string;
  videoId?: string;
  resourceId?: string;
  matchTitle?: string;
  matchHref?: string;
  onCancel?: () => void;
  onSkipDuplicate?: () => void;
  onUploadAnyway?: () => void;
  doneLinks?: { href: string; label: string }[];
};

function progressPercent(loaded: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((loaded / total) * 100));
}

function statusLabel(
  status: UploadProgressStatus,
  kind: "video" | "magazine" | "thumbnail",
): string {
  switch (status) {
    case "queued":
      return "Waiting";
    case "hashing":
      return "Checking file…";
    case "checking":
      return "Checking library…";
    case "duplicate":
      return "Already in library";
    case "presigning":
      return "Preparing…";
    case "downloading":
      return "Downloading video…";
    case "extracting":
      return "Creating poster…";
    case "uploading":
      return kind === "thumbnail" ? "Saving poster…" : "Uploading";
    case "creating":
      return "Saving…";
    case "done":
    case "complete":
      return kind === "thumbnail" ? "Poster created" : "Added to library";
    case "error":
      return "Failed";
  }
}

export function UploadProgressBar({
  fileName,
  kind = "video",
  status,
  loaded = 0,
  total = 0,
  bytesPerSec,
  etaSeconds,
  errorMessage,
  videoId,
  resourceId,
  matchTitle,
  matchHref,
  onCancel,
  onSkipDuplicate,
  onUploadAnyway,
  doneLinks,
}: UploadProgressBarProps) {
  const id = resourceId ?? videoId;
  const links =
    doneLinks ??
    (id
      ? kind === "magazine"
        ? [{ href: `/admin/magazines/${id}/edit`, label: "Edit" }]
        : [{ href: `/videos/${id}/poster`, label: "Choose screenshot" }]
      : []);
  const savingLabel = kind === "magazine" ? "Creating magazine record…" : "Creating video record…";
  const isDone = status === "done" || status === "complete";
  const thumbnailIndeterminate =
    kind === "thumbnail" &&
    (status === "extracting" || status === "uploading");
  const percent = isDone
    ? 100
    : status === "queued" ||
        status === "presigning" ||
        status === "creating" ||
        status === "hashing" ||
        status === "checking" ||
        status === "duplicate" ||
        thumbnailIndeterminate
      ? 0
      : progressPercent(loaded, total);

  const showIndeterminate =
    status === "presigning" ||
    status === "creating" ||
    status === "hashing" ||
    status === "checking" ||
    thumbnailIndeterminate;
  const canCancel =
    kind !== "thumbnail" &&
    (status === "queued" ||
      status === "uploading" ||
      status === "presigning" ||
      status === "hashing" ||
      status === "checking");

  return (
    <div
      className="rounded-lg border border-border bg-muted/40 px-3 py-3"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{fileName}</p>
          <p className="text-xs text-muted-foreground">{statusLabel(status, kind)}</p>
        </div>
        {(canCancel && onCancel) || (status === "duplicate" && onSkipDuplicate) ? (
          <button
            type="button"
            onClick={status === "duplicate" ? onSkipDuplicate : onCancel}
            className="shrink-0 text-xs font-medium text-muted-foreground transition hover:text-foreground"
          >
            {status === "duplicate" ? "Skip" : "Cancel"}
          </button>
        ) : null}
      </div>

      {status !== "duplicate" && status !== "queued" ? (
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-border">
          {showIndeterminate ? (
            <div className="h-full w-1/3 animate-pulse rounded-full bg-primary-600" />
          ) : (
            <div
              className="h-full rounded-full bg-primary-600 transition-[width] duration-150 ease-out"
              style={{ width: `${percent}%` }}
            />
          )}
        </div>
      ) : null}

      <p className="mt-2 text-xs text-muted-foreground">
        {isDone ? (
          <span className="text-green-800 dark:text-green-400">
            {kind === "thumbnail"
              ? "Poster created"
              : `${formatBytes(total || loaded)} uploaded`}
            {links.length > 0 ? (
              <>
                {" · "}
                {links.map((link, index) => (
                  <span key={link.href}>
                    {index > 0 ? " · " : null}
                    <Link
                      href={link.href}
                      className="font-medium underline-offset-2 hover:underline"
                    >
                      {link.label}
                    </Link>
                  </span>
                ))}
              </>
            ) : null}
          </span>
        ) : status === "duplicate" ? (
          <span className="text-amber-800 dark:text-amber-300" role="alert">
            This exact file is already in the library
            {matchTitle ? (
              <>
                {" as "}
                {matchHref ? (
                  <Link
                    href={matchHref}
                    className="font-medium underline-offset-2 hover:underline"
                  >
                    {matchTitle}
                  </Link>
                ) : (
                  <span className="font-medium">{matchTitle}</span>
                )}
              </>
            ) : null}
            . Skip it, or upload another copy.
            {onUploadAnyway ? (
              <>
                {" "}
                <button
                  type="button"
                  onClick={onUploadAnyway}
                  className="font-medium underline-offset-2 hover:underline"
                >
                  Upload anyway
                </button>
              </>
            ) : null}
          </span>
        ) : status === "error" ? (
          <span className="text-red-700 dark:text-red-400" role="alert">
            {errorMessage ??
              (kind === "thumbnail"
                ? "Could not create thumbnail."
                : "Upload failed")}
          </span>
        ) : status === "queued" ? (
          kind === "thumbnail"
            ? "Waiting for the current poster to finish."
            : "Waiting for the current upload to finish."
        ) : status === "extracting" ? (
          "Grabbing a frame from about one second in…"
        ) : kind === "thumbnail" && status === "uploading" ? (
          "Uploading poster…"
        ) : status === "hashing" ? (
          "Fingerprinting file…"
        ) : status === "checking" ? (
          "Looking for the same file in the library…"
        ) : status === "presigning" ? (
          "Getting upload URL…"
        ) : status === "creating" ? (
          savingLabel
        ) : (
          <>
            {percent}% · {formatBytes(loaded)} of {formatBytes(total)}
            {bytesPerSec != null ? ` · ${formatSpeed(bytesPerSec)}` : ""}
            {etaSeconds != null ? ` · ${formatEta(etaSeconds)}` : ""}
          </>
        )}
      </p>
    </div>
  );
}
